// 手机端让表单、记录与操作自然换行，移除固定最小宽度；业务提交与原有权限保持一致。
// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { PhoneOutgoing, PhoneIncoming, PhoneForwarded, RefreshCw, Search, ExternalLink, Mic, Voicemail, FileText, ChevronDown, ChevronRight, PhoneCall, Sparkles, TrendingUp, Clock } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DIRS = {
  outbound: { zh: "呼出", en: "Outbound", icon: PhoneOutgoing, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  inbound: { zh: "呼入", en: "Inbound", icon: PhoneIncoming, cls: "text-blue-700 bg-blue-50 border-blue-200" },
  bridge: { zh: "桥接", en: "Bridge", icon: PhoneForwarded, cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
const STATUS_CLS = {
  completed: "text-emerald-700 bg-emerald-50", "in-progress": "text-blue-700 bg-blue-50",
  ringing: "text-amber-700 bg-amber-50", initiated: "text-slate-600 bg-slate-100",
  "no-answer": "text-red-700 bg-red-50", busy: "text-red-700 bg-red-50",
  failed: "text-red-700 bg-red-50", canceled: "text-slate-500 bg-slate-100",
};
const INTENT_CLS = {
  "高意向": "bg-emerald-100 text-emerald-800", "已约现场": "bg-emerald-100 text-emerald-800",
  "中意向": "bg-blue-100 text-blue-800", "价格敏感": "bg-amber-100 text-amber-800",
  "需回拨": "bg-amber-100 text-amber-800", "低意向": "bg-slate-100 text-slate-600",
  "无效来电": "bg-red-50 text-red-600", "其他": "bg-slate-100 text-slate-500",
};
const FILTERS = ["", "outbound", "inbound", "bridge", "voicemail", "callback"];

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const fmtTime = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function CallHistory() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [canSeeAll, setCanSeeAll] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const [scope, setScope] = useState("self");
  const [dir, setDir] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [heardIds, setHeardIds] = useState({});
  const [brief, setBrief] = useState(null);
  const token = authStore.get("ali_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dir === "voicemail") params.voicemail = true;
      else if (dir === "callback") params.needs_callback = true;
      else if (dir) params.direction = dir;
      if (q.trim()) params.q = q.trim();
      if (agent) params.agent = agent;
      const { data } = await api.get("/telephony/calls", { params });
      setCalls(data.calls || []);
      setCanSeeAll(!!data.canSeeAll);
      setAgents(data.agents || []);
      setScope(data.scope || "self");
      if (data.canSeeAll && !brief) {
        try { const { data: b } = await api.get("/telephony/briefing"); setBrief(b); } catch { /* ignore */ }
      }
    } catch { setCalls([]); }
    setLoading(false);
  }, [api, dir, q, agent, brief]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const callBack = (c) => {
    if (!c.customerNumber) return;
    window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number: c.customerNumber, name: c.name } }));
  };
  const dialNumber = (number, name) => {
    if (!number) return;
    window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number, name } }));
  };

  const filterLabel = (d) => {
    if (!d) return zh ? "全部" : "All";
    if (d === "voicemail") return zh ? "语音信箱" : "Voicemail";
    if (d === "callback") return zh ? "待回拨" : "Callback";
    return zh ? DIRS[d].zh : DIRS[d].en;
  };
  const statusLabel = (s) => {
    const map = { completed: ["已接通", "Completed"], "in-progress": ["通话中", "In call"], ringing: ["响铃中", "Ringing"],
      initiated: ["拨号中", "Initiated"], "no-answer": ["未接听", "No answer"], busy: ["占线", "Busy"],
      failed: ["失败", "Failed"], canceled: ["已取消", "Canceled"] };
    const m = map[s]; return m ? (zh ? m[0] : m[1]) : (s || "—");
  };
  const transcriptText = (c) => {
    if (c.transcript) return c.transcript;
    if (c.transcriptStatus === "processing" || (c.hasRecording && !c.transcriptStatus)) return zh ? "转写中…" : "Transcribing…";
    if (c.transcriptStatus === "failed") return zh ? "转写失败" : "Transcription failed";
    if (c.transcriptStatus === "empty") return zh ? "（无语音内容）" : "(no speech)";
    return zh ? "暂无文字稿" : "No transcript";
  };
  const markHeard = async (c) => {
    if (!c.isVoicemail || c.voicemailHeard || heardIds[c.id]) return;
    setHeardIds((h) => ({ ...h, [c.id]: true }));
    try { await api.post(`/telephony/calls/${c.id}/heard`); } catch { /* ignore */ }
  };
  const cols = canSeeAll ? 9 : 8;

  return (
    <div className="space-y-5" data-testid="call-history-page">
      <div className="flex items-center gap-3 flex-wrap">
        <PhoneIncoming className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "通话记录" : "Call History"}</h1>
        <span className="text-sm text-slate-500">{canSeeAll ? (zh ? "全公司通话" : "All company calls") : (zh ? "我的通话" : "My calls")} · {calls.length}</span>
        <button data-testid="calls-refresh" onClick={load} disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}
        </button>
      </div>

      {canSeeAll && brief && (
        <div data-testid="calls-briefing" className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-800">{zh ? `每日话务简报 · ${fmtD(brief.date)}（昨日）` : `Daily briefing · ${fmtD(brief.date)}`}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["total", zh ? "总通话" : "Total", brief.total], ["answerRate", zh ? "接通率" : "Answer rate", `${brief.answerRate}%`],
              ["missed", zh ? "未接" : "Missed", brief.missed], ["voicemail", zh ? "留言" : "Voicemail", brief.voicemail]].map(([k, label, val]) => (
              <div key={k} data-testid={`brief-${k}`} className="rounded-lg bg-white border border-slate-100 px-3 py-2">
                <p className="text-xs text-slate-400">{label}</p>
                <p className={`text-xl font-bold ${k === "missed" && val > 0 ? "text-red-600" : "text-slate-800"}`}>{val}</p>
              </div>
            ))}
          </div>
          {brief.topCallback && brief.topCallback.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1"><Clock className="h-3 w-3" />{zh ? "待回拨 Top（昨日未回拨）" : "Top to call back"}</p>
              <div className="flex flex-wrap gap-2">
                {brief.topCallback.map((t) => (
                  <button key={t.number} data-testid={`brief-callback-${t.number}`} onClick={() => dialNumber(t.number, t.name)}
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-200 text-amber-800 hover:bg-amber-50 rounded-full pl-3 pr-2 py-1">
                    {t.name || t.number}{t.isVoicemail && <Voicemail className="h-3 w-3" />}
                    <span className="inline-flex items-center gap-0.5 bg-emerald-600 text-white rounded-full px-1.5 py-0.5"><PhoneCall className="h-2.5 w-2.5" />{zh ? "回拨" : "Call"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {FILTERS.map((d) => (
            <button key={d || "all"} data-testid={`calls-filter-${d || "all"}`} onClick={() => setDir(d)}
              className={`px-3 py-1.5 text-sm transition-colors inline-flex items-center gap-1 ${dir === d ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {d === "voicemail" && <Voicemail className="h-3.5 w-3.5" />}{d === "callback" && <PhoneCall className="h-3.5 w-3.5" />}{filterLabel(d)}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input data-testid="calls-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={zh ? "搜索号码/客户/坐席/文字稿/摘要" : "Search number/client/agent/transcript"}
            className="h-9 w-72 rounded-lg border border-slate-200 pl-8 pr-3 text-sm focus:outline-none focus:border-amber-400" />
        </div>
        {agents.length > 1 && (
          <select data-testid="calls-agent-filter" value={agent} onChange={(e) => setAgent(e.target.value)} data-scope={scope}
            className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:border-amber-400">
            <option value="">{scope === "all" ? (zh ? "全部坐席" : "All agents") : (zh ? `本部门全部（${agents.length} 人）` : `Whole department (${agents.length})`)}</option>
            {agents.map((a) => <option key={a.username} value={a.username}>{a.name}{a.username === user.username ? (zh ? "（我）" : " (me)") : ""}</option>)}
          </select>
        )}
        {scope === "dept" && <span className="text-[11px] text-slate-400" data-testid="calls-scope-hint">{zh ? "部门经理：可查看本人与本部门成员的通话" : "Manager: own + department calls"}</span>}
        {scope === "self" && <span className="text-[11px] text-slate-400" data-testid="calls-scope-hint">{zh ? "仅显示我的通话记录" : "Only your calls"}</span>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <ResponsiveTable className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3 font-medium w-6"></th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "时间" : "Time"}</th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "方向" : "Direction"}</th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "对方号码" : "Number"}</th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "客户 / 意向" : "Client / Intent"}</th>
              {canSeeAll && <th className="text-left px-4 py-3 font-medium">{zh ? "坐席" : "Agent"}</th>}
              <th className="text-left px-4 py-3 font-medium">{zh ? "状态/时长" : "Status/Dur"}</th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "录音 / 摘要" : "Recording / Summary"}</th>
              <th className="text-left px-4 py-3 font-medium">{zh ? "回拨" : "Call"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {calls.length === 0 && (
              <tr><td colSpan={cols} className="px-4 py-10 text-center text-slate-400" data-testid="calls-empty">
                {dir === "voicemail" ? (zh ? "暂无语音留言" : "No voicemail") : dir === "callback" ? (zh ? "没有待回拨的来电 ✓" : "No callbacks pending ✓") : (zh ? "暂无通话记录" : "No calls yet")}
              </td></tr>
            )}
            {calls.map((c) => {
              const d = DIRS[c.direction] || DIRS.outbound;
              const DIcon = d.icon;
              const isOpen = !!expanded[c.id];
              const unheard = c.isVoicemail && !c.voicemailHeard && !heardIds[c.id];
              return (
                <React.Fragment key={c.id}>
                  <tr data-testid={`call-row-${c.id}`} className="hover:bg-slate-50 align-top">
                    <td className="px-2 py-3">
                      <button data-testid={`call-expand-${c.id}`} onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
                        className="text-slate-400 hover:text-slate-700">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(c.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs border rounded-full px-2 py-0.5 ${d.cls}`}>
                          <DIcon className="h-3 w-3" />{zh ? d.zh : d.en}
                        </span>
                        {c.isVoicemail && (
                          <span data-testid={`call-vm-${c.id}`} className="inline-flex items-center gap-1 text-xs border border-purple-200 bg-purple-50 text-purple-700 rounded-full px-2 py-0.5">
                            <Voicemail className="h-3 w-3" />{zh ? "留言" : "VM"}
                            {unheard && <span data-testid={`call-vm-new-${c.id}`} className="ml-0.5 h-1.5 w-1.5 rounded-full bg-red-500" />}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700 whitespace-nowrap">{c.customerNumber || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {c.leadId ? (
                          <button data-testid={`call-lead-${c.id}`} onClick={() => navigate(`/crm/leads/${c.leadId}`)}
                            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 hover:underline w-fit">
                            {c.name || (zh ? "查看客户" : "View lead")}<ExternalLink className="h-3 w-3" />
                          </button>
                        ) : (c.name || <span className="text-slate-400">—</span>)}
                        {c.intentTag && (
                          <span data-testid={`call-intent-${c.id}`} className={`w-fit text-[11px] rounded-full px-2 py-0.5 ${INTENT_CLS[c.intentTag] || "bg-slate-100 text-slate-500"}`}>{c.intentTag}
                            {typeof c.score === "number" && <span data-testid={`call-score-${c.id}`} className="ml-1 font-bold">· {c.score}分</span>}
                          </span>
                        )}
                        {!c.intentTag && typeof c.score === "number" && (
                          <span data-testid={`call-score-${c.id}`} className="w-fit text-[11px] rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 font-bold">{c.score}分</span>
                        )}
                      </div>
                    </td>
                    {canSeeAll && <td className="px-4 py-3 text-slate-600">{c.agentName || c.agentUsername || "—"}</td>}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block text-xs rounded-full px-2 py-0.5 ${STATUS_CLS[c.status] || "text-slate-600 bg-slate-100"}`}>{statusLabel(c.status)}</span>
                      <span className="text-slate-400 ml-1.5">{fmtDur(c.durationSec)}</span>
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      {c.hasRecording ? (
                        <audio data-testid={`call-recording-${c.id}`} controls preload="none" onPlay={() => markHeard(c)} className="h-11 sm:h-8 w-full sm:w-56 max-w-[220px]"
                          src={`${BACKEND}/api/telephony/calls/${c.id}/recording?auth=${token}`} />
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Mic className="h-3 w-3" />{zh ? "无" : "None"}</span>
                      )}
                      {c.summary && <p data-testid={`call-summary-${c.id}`} className="mt-1 text-xs text-slate-500 line-clamp-2">{c.summary}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {c.customerNumber && (
                        <button data-testid={`call-callback-${c.id}`} onClick={() => callBack(c)} title={zh ? "用软电话回拨" : "Call back"}
                          className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5 py-1.5">
                          <PhoneCall className="h-3.5 w-3.5" />{zh ? "回拨" : "Call"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`call-transcript-row-${c.id}`} className="bg-slate-50/70">
                      <td></td>
                      <td colSpan={cols - 1} className="px-4 pb-4 pt-1">
                        {c.summary && (
                          <div className="flex items-start gap-2 text-slate-700 mb-2">
                            <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-slate-500 mb-0.5">{zh ? "AI 通话要点" : "AI summary"}</p>
                              <p className="text-sm">{c.summary}{c.intentTag && <span className={`ml-2 text-[11px] rounded-full px-2 py-0.5 ${INTENT_CLS[c.intentTag] || "bg-slate-100 text-slate-500"}`}>{c.intentTag}</span>}{typeof c.score === "number" && <span className="ml-2 text-[11px] font-bold text-amber-700">话术评分 {c.score}/100</span>}</p>
                              {c.tips && c.tips.length > 0 && (
                                <ul data-testid={`call-tips-${c.id}`} className="mt-1.5 space-y-0.5">
                                  {c.tips.map((t, i) => <li key={i} className="text-xs text-slate-500 flex items-start gap-1"><span className="text-amber-500">·</span>{t}</li>)}
                                </ul>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2 text-slate-700">
                          <FileText className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-slate-500 mb-1">{zh ? "通话文字稿（Whisper 自动转写）" : "Transcript (Whisper)"}</p>
                            <p data-testid={`call-transcript-${c.id}`} className="text-sm whitespace-pre-wrap leading-relaxed">{transcriptText(c)}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </ResponsiveTable>
      </div>
      <p className="text-xs text-slate-400">
        {zh ? "录音结束后自动归档、Whisper 转写并生成 AI 要点+意向标签；点「回拨」用浏览器软电话直接拨号。展开 ▸ 查看文字稿。"
            : "Recordings are archived, transcribed and AI-summarised after each call. Click Call to dial back. Expand ▸ for transcript."}
      </p>
    </div>
  );
}
